import { useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'

import { subnetRepository } from '../../infrastructure/subnetRepo'

type DeleteSubnetParams = {
  id: string
  isAssigned: boolean
}

export function useDeleteSubnet() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, isAssigned }: DeleteSubnetParams) => {
      const result = await subnetRepository.remove(id)

      if (!result.response.ok) {
        // An assigned subnet is refused as a bare 404 carrying only the
        // subnet id — the backend has no typed refusal yet — so the real
        // reason is named here instead of echoing the id.
        if (result.response.status === 404 && isAssigned) {
          throw new Error(
            'The subnet is assigned to a cluster — the backend cannot delete it, and unassigning does not exist yet.',
          )
        }
        // Every backend error body is {"error": "<string>"}; the spec does
        // not model it, so it is read untyped.
        const body = result.error as unknown as { error?: string } | undefined
        throw new Error(
          body?.error ||
            `Subnet could not be deleted (HTTP ${result.response.status})`,
        )
      }

      return id
    },

    onError: (error: Error) => {
      toast.error(error.message)
    },

    onSuccess: () => {
      toast.success('Subnet was deleted')
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['subnets'] })
    },
  })
}
